import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Users, Sparkles, ArrowLeft } from 'lucide-react';
import { portalApi, portalAuth } from '@/lib/portalApi';

export default function PortalLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicProcessing, setMagicProcessing] = useState(false);

  // If already logged in, redirect
  useEffect(() => {
    const u = portalAuth.getUser();
    if (u) {
      navigate(u.portalRole === 'family_principal' ? '/portal/family' : '/portal/adviser', { replace: true });
    }
  }, [navigate]);

  // Auto-process magic token
  useEffect(() => {
    const magic = params.get('magic');
    if (magic && !magicProcessing) {
      setMagicProcessing(true);
      portalApi.magicLogin(magic)
        .then(res => {
          portalAuth.set(res.data.token, res.data.user);
          toast.success(`Welcome, ${res.data.user.name}`);
          navigate(res.data.user.portalRole === 'family_principal' ? '/portal/family' : '/portal/adviser', { replace: true });
        })
        .catch(err => {
          toast.error(err.response?.data?.detail || 'Magic link invalid or expired');
          setMagicProcessing(false);
        });
    }
  }, [params, navigate, magicProcessing]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await portalApi.login(email, password);
      portalAuth.set(res.data.token, res.data.user);
      toast.success(`Welcome, ${res.data.user.name}`);
      navigate(res.data.user.portalRole === 'family_principal' ? '/portal/family' : '/portal/adviser', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  if (magicProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F5]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32] mx-auto mb-3" />
          <p className="text-sm text-[#5D524D]">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#F9F7F5] via-white to-[#FFF8F0] p-4" data-testid="portal-login-page">
      <div className="w-full max-w-5xl mx-auto pt-2">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-[#8C8078] hover:text-[#A86A3A] px-2 py-1 transition-colors"
          data-testid="portal-login-back-to-website-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to QIndex One
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center">
       <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#A86A3A] to-[#4A3B32] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl text-[#2C2420]">QIndex One</span>
          </Link>
          <h1 className="font-heading text-2xl text-[#2C2420] mb-2">Portal Sign In</h1>
          <p className="text-sm text-[#8C8078]">For external advisers and family principals</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E6E0D9] p-6 space-y-4 shadow-sm">
          <div className="space-y-1.5">
            <Label htmlFor="portal-email">Email</Label>
            <Input
              id="portal-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@firm.com"
              required
              data-testid="portal-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="portal-pw">Password</Label>
            <Input
              id="portal-pw"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              data-testid="portal-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4A3B32] text-white hover:bg-[#3a2e26]"
            data-testid="portal-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Sign In
          </Button>
        </form>

        <div className="mt-6 grid grid-cols-2 gap-3 text-center">
          <div className="bg-white/60 backdrop-blur border border-[#E6E0D9] rounded-xl p-3">
            <Users className="w-5 h-5 text-[#A86A3A] mx-auto mb-1" />
            <p className="text-[10px] text-[#5D524D]">External Advisers</p>
            <p className="text-[9px] text-[#8C8078]">Read & comment</p>
          </div>
          <div className="bg-white/60 backdrop-blur border border-[#E6E0D9] rounded-xl p-3">
            <ShieldCheck className="w-5 h-5 text-[#A86A3A] mx-auto mb-1" />
            <p className="text-[10px] text-[#5D524D]">Family Principals</p>
            <p className="text-[9px] text-[#8C8078]">Read-only</p>
          </div>
        </div>

        <p className="text-center text-xs text-[#8C8078] mt-6">
          Are you a team member? <Link to="/login" className="text-[#A86A3A] hover:underline">Sign in here</Link>
        </p>
      </div>
      </div>
    </div>
  );
}
